"use client";

import { useUser } from "@clerk/nextjs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { getAccountMenuLinks } from "@/data/account-menu";
import { privateProps } from "@/features/analytics/lib/masking";
import { useSignOut } from "@/features/auth/hooks/use-sign-out";
import { AUTH_SECONDARY_BUTTON } from "@/features/auth/lib/auth-button-styles";
import { UserAvatar } from "@/components/header/user-avatar";
import { Link } from "@/i18n/navigation";
import type { HeaderThemeType } from "@/types/common.types";

export const AccountMenu = ({ theme }: { theme?: HeaderThemeType }) => {
  const t = useTranslations("header");
  const { user } = useUser();
  const links = getAccountMenuLinks();
  const signOut = useSignOut();

  const email = user?.primaryEmailAddress?.emailAddress;
  const fullName = user?.fullName ?? email;
  const isLightHeader = theme === "light" || theme === "all-light";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t("accountMenu")}
        className="flex h-12 items-center gap-2 rounded-xl px-5 py-3 text-current"
        data-pw="account-menu-button"
      >
        <UserAvatar
          className="size-6"
          compact
          variant={isLightHeader ? "dark" : "light"}
        />
        <span className="font-medium text-base" {...privateProps}>
          {user?.firstName ?? fullName}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-82 gap-6 rounded-xl border-border p-4 shadow-[0_2px_3px_0_rgba(0,0,0,0.08)]"
        data-pw="account-menu"
        sideOffset={12}
      >
        <div className="flex items-center gap-3 px-4 py-2">
          <UserAvatar className="size-12" />
          <div
            className="flex min-w-0 flex-col justify-center"
            {...privateProps}
          >
            <span className="truncate text-base text-text leading-6">
              {fullName}
            </span>
            <span className="truncate text-sm text-text-subtle leading-[22px]">
              {email}
            </span>
          </div>
        </div>
        <DropdownMenuSeparator className="mx-0 my-2" />
        {links.map((link) => (
          <DropdownMenuItem
            asChild
            className="cursor-pointer gap-2 rounded-lg p-4 text-base text-text focus:bg-background-fill-primary-hover"
            key={link.label}
          >
            <Link data-pw={link.testData} href={link.href}>
              {link.icon}
              <span className="leading-6">{link.label}</span>
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="mx-0 mt-2 mb-6" />
        <Button
          className={AUTH_SECONDARY_BUTTON}
          data-pw="sign-out-button"
          onClick={signOut}
          type="button"
          variant="ghost"
        >
          {t("signOut")}
        </Button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
